import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import {
  IntegrationWithJobInfo,
  getIntegrationsWithJobs,
  findIntegrationByName,
  findEmailIntegrationForUsername,
  UnfiConfig,
} from "@/api/integration"
import { LoadingSpinner } from "@/app/loading"
import { Button } from "@/components/ui/button"
import { useLocation } from "wouter-preact"
import { SettingsLayout } from "@/settings/layout"
import { IntegrationSettingsLayout, SettingsSection } from "../layout"
import { SyncStatusDisplay, IntegrationLogo } from "../sync-status-row"
import { WeeklyStatusReportSettings } from "@/settings/weekly_status_report"
import { PaymentHistoryReportSettings } from "@/settings/payment_history_report"
import unfiLogo from "/img/integration/unfi.png"
import gmailLogo from "/img/integration/gmail.png"
import microsoftLogo from "/img/integration/microsoft.png"
import { UnfiConfigForm } from "@/setup/config/unfi/page"
import { BackToSettingsButton } from "@/setup/connect/settings-connect-layout"
import { ContractsList } from "@/contracts/contracts-list"
import { ConnectMailboxButtons } from "@/connect_mailbox/connect_mailbox_buttons"
import { is_auth_failure } from "@/settings/sync_health"

export function UnfiSettingsPage() {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [emailIntegration, setEmailIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    const res = await getIntegrationsWithJobs()
    if (res.ok) {
      const integrations = res.value.data
      const emails = integrations.filter(i => ["gmail", "microsoft"].includes(i.name))
      setEmailAccounts(emails)

      const found = findIntegrationByName(integrations, "unfi")
      if (found) {
        setIntegration(found)
        const unfiConfig = found.data as unknown as UnfiConfig
        if (unfiConfig.email_address) {
          const emailInt = findEmailIntegrationForUsername(integrations, unfiConfig.email_address)
          if (emailInt) {
            setEmailIntegration(emailInt)
          }
        }
      }
    }
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <SettingsLayout>
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </SettingsLayout>
    )
  }

  if (!integration) {
    return (
      <SettingsLayout>
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">UNFI integration not found</p>
          <Button variant="outline" onClick={() => setLocation("/settings/connections")}>
            Back to Connections
          </Button>
        </div>
      </SettingsLayout>
    )
  }

  return (
    <IntegrationSettingsLayout
      logo={unfiLogo}
      name="UNFI"
      username={integration.username}
      integrationId={integration.id}>
      {/* Sync Status - shows the email integration's sync status */}
      <SettingsSection title="Sync Status">
        {emailIntegration ? (
          <div class="space-y-3">
            <div class="flex items-center gap-4">
              <IntegrationLogo
                src={emailIntegration.name === "gmail" ? gmailLogo : microsoftLogo}
                alt={emailIntegration.name}
              />
              <span class="text-sm">{emailIntegration.username}</span>
            </div>
            <SyncStatusDisplay lastSyncJob={emailIntegration.last_sync_job} integrationName={emailIntegration.name} />
            {/* The mailbox is named already, so the reconnect is one click plus the provider sign-in. */}
            {is_auth_failure(emailIntegration.last_sync_job?.error) && (
              <ConnectMailboxButtons
                microsoftLabel="Connect Microsoft Office 365"
                defaultEmail={emailIntegration.username}
                className="max-w-sm"
              />
            )}
          </div>
        ) : (
          <p class="text-sm text-red-500 font-medium">
            No email integration found. Please configure an email address in the settings below.
          </p>
        )}
      </SettingsSection>

      <SettingsSection title="Configuration">
        <UnfiConfigForm integration={integration} emailAccounts={emailAccounts} onSuccess={() => {}} />
      </SettingsSection>

      <SettingsSection title="Weekly Status Report">
        <WeeklyStatusReportSettings />
      </SettingsSection>

      <SettingsSection title="Payment History Report">
        <PaymentHistoryReportSettings />
      </SettingsSection>

      <SettingsSection title="Contract Documents">
        <ContractsList source="unfi" integrationId={integration.id} />
      </SettingsSection>
    </IntegrationSettingsLayout>
  )
}

export function SettingsConfigUnfiPage() {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<IntegrationWithJobInfo | null>(null)
  const [emailAccounts, setEmailAccounts] = useState<IntegrationWithJobInfo[]>([])
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    const res = await getIntegrationsWithJobs()
    if (res.ok) {
      const integrations = res.value.data
      const emails = integrations.filter(i => ["gmail", "microsoft"].includes(i.name))
      setEmailAccounts(emails)

      const found = findIntegrationByName(integrations, "unfi")
      if (found) {
        setIntegration(found)
      }
    }
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <div class="px-4">
        <BackToSettingsButton />
        <div class="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (!integration) {
    return (
      <div class="px-4">
        <BackToSettingsButton />
        <div class="text-center py-12">
          <p class="text-gray-500 mb-4">UNFI integration not found. Please connect UNFI first.</p>
          <Button variant="outline" onClick={() => setLocation("/settings/config/unfi")}>
            Configure UNFI
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div class="px-4">
      <BackToSettingsButton />
      <div class="max-w-lg mx-auto pt-12">
        <img src={unfiLogo} alt="UNFI" class="h-10 object-contain mx-auto mb-8" />
        <UnfiConfigForm
          integration={integration}
          emailAccounts={emailAccounts}
          onSuccess={() => setLocation("/settings/connections")}
        />
      </div>
    </div>
  )
}
